<template>
  <div
    class="ca-header__megamenu"
    :class="{ open: hoveredIndex !== null, loading: isLoading }"
    @mouseenter="emit('keep-open')"
    @mouseleave="emit('close-menu')"
  >
    <hr class="ca-header__megamenu-separator" />

    <!-- Shimmer Loading State -->
    <div
      v-if="isLoading"
      class="ca-header__megamenu-inner pt-size-5 pr-size-4 pb-size-6 pl-size-4"
    >
      <!-- <div class="">
        <div
          class="ca-header__megamenu-header-title px-size-5 py-size-3 caption-action"
        ></div>
      </div> -->
      <div class="ca-header__megamenu-columns">
        <div
          v-for="(section, sIdx) in shimmerCategories"
          :key="`shimmer-col-${sIdx}`"
          class="ca-header__megamenu-section"
        >
          <p
            class="ca-header__megamenu-section-title px-size-5 py-size-3 caption-large"
          ></p>
          <ul class="ca-header__megamenu-list">
            <li
              v-for="(link, lIdx) in section.items"
              :key="`shimmer-link-${sIdx}-${lIdx}`"
            >
              <div class="ca-header__megamenu-link caption-medium">
                <span></span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Real Content -->
    <div
      v-else-if="hoveredIndex !== null && activeItem"
      class="ca-header__megamenu-inner pt-size-5 pr-size-4 pb-size-6 pl-size-4"
    >
      <!-- <div class="">
        <a
          :href="activeItem.url"
          class="ca-header__megamenu-header-title px-size-5 py-size-3 gap-size-3 caption-action"
        >
          {{ activeItem.title }}
          <component :is="ArrowRight" class="ca-header__megamenu-link--arrow" />
        </a>
      </div> -->
      <div class="ca-header__megamenu-columns">
        <div
          v-for="(category, sIdx) in activeItem.categories"
          :key="sIdx"
          class="ca-header__megamenu-section"
        >
          <a
            :href="category.url || null"
            :target="category.target || '_self'"
            class="ca-header__megamenu-section-title px-size-5 py-size-3 caption-large"
            :class="{
              'ca-header__megamenu-section-title--disabled': !category.url,
            }"
            @mouseenter="hoveredSection = sIdx"
            @mouseleave="!isRestoringState ? (hoveredSection = null) : null"
          >
            {{ category.title }}
            <component
              v-if="category.url"
              :is="ArrowRight"
              class="ca-header__megamenu-link--arrow"
              :class="{ 'is-visible': hoveredSection === sIdx }"
            />
          </a>

          <ul class="ca-header__megamenu-list">
            <li
              v-for="(item, lIdx) in category.items"
              :key="`${sIdx}-${lIdx}`"
              class="rounded-xs"
            >
              <a
                :href="item.url"
                :target="item.target || '_self'"
                class="ca-header__megamenu-link caption-medium px-size-5 py-size-2"
                @mouseenter="hoveredLink = `${sIdx}-${lIdx}`"
                @mouseleave="!isRestoringState ? (hoveredLink = null) : null"
              >
                <span>{{ item.title }}</span>
                <CaStatusTag v-if="item.tag" :label="item.tag" />
                <component
                  :is="ArrowRight"
                  class="ca-header__megamenu-link--arrow"
                  :class="{ 'is-visible': hoveredLink === `${sIdx}-${lIdx}` }"
                />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import ArrowRight from "@assets/arrow-right-green.svg";
import CaStatusTag from "../../base/tags/CaStatusTag.vue";

const props = defineProps({
  homeItems: {
    type: Array,
    default: () => [],
  },
  hoveredIndex: {
    type: Number,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isRestoringState: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["keep-open", "close-menu"]);

const hoveredSection = ref(null);
const hoveredLink = ref(null);

const activeItem = computed(() =>
  props.hoveredIndex !== null ? props.homeItems?.[props.hoveredIndex] : null,
);

const shimmerCategories = computed(
  () => props.homeItems?.[0]?.categories || [],
);
</script>

<style scoped lang="scss">
.ca-header__megamenu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-bottom-right-radius: var(--size-4);
  border-bottom-left-radius: var(--size-4);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(-14px);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease,
    visibility 0s linear 0.25s;
  z-index: 100;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  li:hover {
    background: var(--ca-color-hover-pressed-container-high-hover);
  }

  li:active {
    background: var(--ca-color-hover-pressed-container-high-hover);
  }

  &.open {
    opacity: 1;
    visibility: visible;
    pointer-events: all;
    box-shadow: 0 6px 6px -2px rgba(0, 0, 0, 0.1);
    max-height: calc(100vh - 140px);
    overflow-y: auto;
    overscroll-behavior: contain;
    transition:
      opacity 0.25s ease,
      transform 0.25s ease,
      visibility 0s linear 0s;
    scrollbar-width: thin;
  }

  &-inner {
    margin-left: var(--size-5);
    display: flex;
    flex-direction: column;
    gap: var(--size-4);

    ul {
      padding-inline-start: 0;
    }
  }

  &-header-title {
    color: var(--ca-color-text-primary);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    letter-spacing: var(--letter-spacing-sm);
    cursor: pointer;
    min-height: 48px;
    align-self: start;

    &:hover {
      border-radius: var(--size-3);
      background: var(--ca-color-hover-pressed-container-high-hover);
    }
    &:active {
      background: var(--ca-color-hover-pressed-inverted-hover-light);
      border-radius: var(--size-3);
    }
    &:focus-visible {
      outline: 1px solid var(--ca-color-feedback-info);
    }
  }

  &-separator {
    border: none;
    border-top: 1px solid var(--ca-color-text-light);
    margin: var(--size-5) var(--size-4) 0 var(--size-4);
  }

  &-columns,
  &-header {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--size-6);
  }

  &-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &-section-title {
    color: var(--ca-color-text-body);
    letter-spacing: var(--letter-spacing-sm);
    margin: 0;
    min-height: 48px;
    display: flex;
    align-items: center;
    cursor: pointer;
    text-decoration: none;
    background: none;
    border: none;

    &:focus-visible {
      outline: 1px solid var(--ca-color-feedback-info);
    }
    &:active {
      background: var(--ca-color-hover-pressed-inverted-hover-light) !important;
      border-radius: var(--size-3) !important;
    }
    &:hover {
      border-radius: var(--size-3);
      background: var(--ca-color-hover-pressed-container-high-hover);
    }
  }

  &-section-title--disabled {
    cursor: default;
    pointer-events: none;

    &:hover,
    &:active {
      background: none !important;
    }
  }

  &-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  &-link {
    color: var(--ca-color-text-body-variant);
    letter-spacing: var(--letter-spacing-xs);
    text-decoration: none;
    display: flex;
    width: 100%;
    align-items: center;
    gap: var(--size-3);
    min-height: 40px;
    cursor: pointer;
    background: none;
    border: none;

    &:focus-visible {
      outline: 1px solid var(--ca-color-feedback-info);
    }
    &--arrow {
      margin-left: auto;
      pointer-events: none;
      flex-shrink: 0;
      opacity: 0;
      visibility: hidden;

      &.is-visible {
        opacity: 1;
        visibility: visible;
      }
    }
  }
}

// Shimmer loading
@keyframes shimmer {
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
}

%shimmer-block {
  background: linear-gradient(
    270deg,
    #fcfdfb 0%,
    var(--Colors-CAEmpresas-SolarizedMedium, #edecea) 50%,
    #fcfdfb 100%
  );
  background-size: 200% auto;
  animation: shimmer 2.5s linear infinite;
  border-radius: var(--size-2);
}

.ca-header__megamenu {
  &.loading .ca-header__megamenu-section-title {
    @extend %shimmer-block;
    height: 14px;
    width: 80px;
  }

  &.loading .ca-header__megamenu-link {
    @extend %shimmer-block;
    height: 14px;
    width: 120px;
  }

  &.loading .ca-header__megamenu-header-title {
    @extend %shimmer-block;
    height: 20px;
    width: 160px;
  }
}
</style>
